import type { BrandDefinition, BrandIdentity, BrandStatus, DesignConfiguration } from '../brand.types.js'

/** Stable machine-readable codes of the brand runtime (TO-367, capability `brand-runtime`). */
export enum BrandRuntimeErrorCode {
	/** Production mode was given no brand to include. */
	NoBrands = 'BRAND_RUNTIME_NO_BRANDS',
	/** A listed brand ID has no directory under the brands root. */
	UnknownBrand = 'BRAND_RUNTIME_UNKNOWN_BRAND',
	/** A listed brand failed to compile in production mode. */
	CompileFailed = 'BRAND_RUNTIME_COMPILE_FAILED',
	/** A brand hook ran outside any `BrandProvider`. */
	ProviderMissing = 'BRAND_PROVIDER_MISSING',
	/** `BrandProvider` was given an artifact that failed to compile. */
	ArtifactInvalid = 'BRAND_ARTIFACT_INVALID',
	/** A configured component's selection has no implementation in its registry (TO-368). */
	DesignCapabilityUnresolved = 'DESIGN_CAPABILITY_UNRESOLVED',
}

/** A brand that compiled: the normalized definition with `data:` asset URLs, plus its theme. */
export interface ValidBrandRuntimeArtifact {
	valid: true
	id: string
	/** `identity.name`. */
	name: string
	status: BrandStatus
	/** The compiled definition. Every `identity.assets.*.src` is a `data:` URL of the asset bytes. */
	definition: BrandDefinition
	/** The compiled `theme.css`, byte-identical to the S1 artifact. */
	themeCss: string
	/** The compiled `tokens.json`: every token path → its resolved value. */
	tokens: Readonly<Record<string, string>>
}

/** A brand that failed to compile (development mode only): its formatted error diagnostics. */
export interface InvalidBrandRuntimeArtifact {
	valid: false
	id: string
	/** One `formatDiagnostic` line per error, in compiler order. */
	diagnostics: readonly string[]
}

/** What `virtual:today-brands` exports per brand and what `BrandProvider` binds. */
export type BrandRuntimeArtifact = ValidBrandRuntimeArtifact | InvalidBrandRuntimeArtifact

/** The resolved theme of a bound brand. */
export interface ResolvedTheme {
	/** The brand's `theme.css`. */
	css: string
	/** Every token path → its resolved value. */
	tokens: Readonly<Record<string, string>>
}

/** The semantic brand configuration a `BrandProvider` exposes (tech spec §17). */
export interface BrandRuntime {
	brand: BrandDefinition
	identity: BrandIdentity
	theme: ResolvedTheme
	design: DesignConfiguration
}
